import React from "react";
import Lottie from "react-lottie";

import cx from "classnames";
import { merge, noop } from "lodash";
import type { AnimationConfigWithData } from "lottie-web";

import styles from "./css/RangeInput.module.css";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnimationOptions = Partial<AnimationConfigWithData> & { animationData: any };

export interface RangeInputItemProps<T extends string> {
	value: T;
	selected: boolean;
	selectedValue: T;
	hoveredValue: T | undefined;
	readonly: boolean;
}

type RangeItemComponent<T extends string> = React.ComponentType<RangeInputItemProps<T>>;

const defaultAnimationOptions = {
	loop: false,
	autoplay: true,
	rendererSettings: {
		preserveAspectRatio: "xMidYMid slice",
	},
};

interface ItemProps<T extends string> {
	animating: boolean;
	selected: boolean;
	name: string;
	onClick: (value: T | null) => void;
	onComplete: () => void;
	value: T;
	animationOptions: AnimationOptions;
	lottieProps: Partial<React.ComponentProps<typeof Lottie>>;
	containerProps: React.HTMLProps<HTMLLabelElement>;
	getOnMouseEnter: (value: T) => React.MouseEventHandler<HTMLLabelElement>;
	getOnMouseLeave: (value: T) => React.MouseEventHandler<HTMLLabelElement>;
	readonly: boolean;
	small: boolean;
}

const Item = <T extends string>({
	children,
	animating,
	selected,
	name,
	onClick,
	onComplete,
	value,
	animationOptions,
	lottieProps,
	containerProps,
	getOnMouseEnter,
	getOnMouseLeave,
	readonly,
	small,
}: React.PropsWithChildren<ItemProps<T>>) => {
	let lottie = null;
	// TODO: enable lottie animations
	if (animating && selected && !true) {
		const {
			// @ts-ignore (react-lottie does not declare the style property)
			style,
			...lottiePropsRest
		} = lottieProps;

		lottie = (
			<Lottie
				isClickToPauseDisabled={true}
				isStopped={false}
				eventListeners={[{ eventName: "complete", callback: onComplete }]}
				options={merge({}, defaultAnimationOptions, animationOptions)}
				// Add default margin value to possibly provided lottie props style
				{...{ style: { margin: "0", ...(style || {}) }, ...lottiePropsRest }}
			/>
		);
	}

	function onInputClick() {
		if (selected) return onClick(null);

		return onClick(value);
	}

	const id = `${name}-${value}`.replace(" ", "_");
	return (
		<>
			{/* Radio buttons are disabled since they cannot be readonly */}
			<input
				type="radio"
				disabled={readonly}
				id={id}
				value={value}
				name={name}
				onClick={onInputClick}
				onChange={noop}
				checked={selected}
				className={styles.radioButton}
			/>
			<label
				htmlFor={id}
				onMouseEnter={readonly ? noop : getOnMouseEnter(value)}
				onMouseLeave={readonly ? noop : getOnMouseLeave(value)}
				{...containerProps}
				className={cx(containerProps.className, styles.item, { [styles.readonly]: readonly, [styles.small]: small })}
			>
				{lottie}
				{children}
			</label>
		</>
	);
};

interface BaseRangeInputProps<T extends string> {
	itemComponent: RangeItemComponent<T>;
	onChange?: (selected: T | null) => void;
	animationOptions: AnimationOptions;
	lottieProps?: Partial<React.ComponentProps<typeof Lottie>>;
	containerProps?: React.HTMLProps<HTMLLabelElement>;
	name: string;
	value: T;
	options: T[];
	readonly?: boolean;
	small?: boolean;
	inline?: boolean;
}

type RangeInputDivProps = Omit<React.HTMLProps<HTMLDivElement>, "ref" | keyof BaseRangeInputProps<string>>;

export type RangeInputProps<T extends string> = BaseRangeInputProps<T> & RangeInputDivProps;

export type RangeInputConsumerProps<T extends string> = Omit<
	RangeInputProps<T>,
	"itemComponent" | "animationOptions" | "options" | "lottieProps" | "containerProps"
>;

interface State<T> {
	animating: boolean;
	hoveredValue: T | undefined;
}

export default class RangeInput<T extends string> extends React.Component<
	RangeInputProps<T> & Omit<React.HTMLProps<HTMLDivElement>, "onChange">,
	State<T>
> {
	state: State<T> = { animating: false, hoveredValue: undefined };

	onClick = (value: T | null) => {
		if (!this.props.readonly && this.props.onChange) {
			this.props.onChange(value);
			this.setState({ animating: true });
		}
	};

	onComplete = () => {
		this.setState({ animating: false });
	};

	getOnMouseEnter = (value: T) => (_e: React.MouseEvent<HTMLLabelElement>) => {
		this.setState({ hoveredValue: value });
	};

	getOnMouseLeave = (value: T) => (_e: React.MouseEvent<HTMLLabelElement>) => {
		this.setState(({ hoveredValue }) => ({
			hoveredValue: hoveredValue === value ? undefined : hoveredValue,
		}));
	};

	render() {
		const {
			animationOptions,
			itemComponent,
			lottieProps,
			containerProps,
			className,
			options,
			value,
			name,
			readonly,
			small,
			inline,
			onChange: _onChange, // remove onChange from divProps
			...divProps
		} = this.props;
		const { animating, hoveredValue } = this.state;
		const Component = itemComponent;

		return (
			<div role="radiogroup" className={cx(styles.wrapper, className, { [styles.inline]: !!inline })} {...divProps}>
				{options.map(option => {
					const selected = value === option;

					return (
						<Item
							key={`${name}-${option}`}
							name={name}
							selected={selected}
							animating={animating}
							onClick={this.onClick}
							onComplete={this.onComplete}
							value={option}
							animationOptions={animationOptions}
							lottieProps={lottieProps || {}}
							containerProps={containerProps || {}}
							getOnMouseEnter={this.getOnMouseEnter}
							getOnMouseLeave={this.getOnMouseLeave}
							readonly={!!readonly}
							small={!!small}
						>
							<Component
								selected={selected}
								hoveredValue={hoveredValue}
								selectedValue={value}
								value={option}
								readonly={!!readonly}
							/>
						</Item>
					);
				})}
			</div>
		);
	}
}
